import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faEye,
    faSearch,
    faRefresh,
    faShoppingCart,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faCalendarAlt,
    faFilePdf,
    faPrint,
    faDownload,
    faCalendar,
    faTimesCircle,
    faUser,
    faPhone,
    faEnvelope,
    faMapMarkerAlt,
    faBuilding,
    faGlobe,
    faUserTag,
    faStickyNote,
    faIdCard,
    faSave,
    faBan,
    faUserEdit,
    faImage,
    faHashtag,
    faUserPlus,
    faExternalLinkAlt,
    faLink,
    faStore,
    faExchangeAlt,
    faBox,
    faDollarSign,
    faMoneyBillWave,
    faCreditCard,
    faWallet,
    faUniversity,
    faReceipt,
    faHistory,
    faCalculator,
    faBalanceScale,
    faPercent,
    faInfoCircle,
    faRulerCombined,
    faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// Create translator instance
const createSellListTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Sales List");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        salesList: 'Sales List',
        shopSales: 'Shop Sales',
        shopName: 'Shop',
        shopCode: 'Shop Code',
        currentShop: 'Current Shop',
        searchPlaceholder: 'Search sales in shop',
        refresh: 'Refresh',
        loadingSales: 'Loading shop sales...',
        noSalesFound: 'No sales found in shop',
        saleDetails: 'Sale Details',
        close: 'Close',
        serialNo: 'S.No',
        date: 'Date',
        billNumber: 'Bill No',
        customerName: 'Customer',
        phone: 'Phone',
        items: 'Items',
        grandTotal: 'Total',
        paidAmount: 'Paid',
        balance: 'Balance',
        paymentMethod: 'Payment',
        paymentStatus: 'Status',
        actions: 'Actions',
        addedBy: 'Added By',
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',
        print: 'Print',
        deletedSuccess: 'Sale deleted successfully',
        deleteError: 'Error deleting sale',
        networkError: 'Network error occurred',
        noShopSelected: 'No shop selected. Please select a shop first.',
        saleId: 'Sale ID',
        customerDetails: 'Customer Details',
        notProvided: 'Not provided',
        paid: 'Paid',
        unpaid: 'Unpaid',
        partiallyPaid: 'Partially Paid',
        cash: 'Cash',
        card: 'Card',
        upi: 'UPI',
        bankTransfer: 'Bank Transfer',
        showing: 'Showing',
        of: 'of',
        sales: 'sales',
        previous: 'Previous',
        next: 'Next',
        filter: 'Filter',
        all: 'All',
        paidFilter: 'Paid',
        unpaidFilter: 'Unpaid',
        partiallyPaidFilter: 'Partially Paid',
        filterBy: 'Filter By',
        billNoFilter: 'Bill Number',
        customerFilter: 'Customer Name',
        phoneFilter: 'Phone',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        exportToPDF: 'PDF',
        addedByFilter: 'Added By',
        editSale: 'Edit Sale',
        updateSale: 'Update Sale',
        updating: 'Updating...',
        saleUpdated: 'Sale updated successfully',
        updateError: 'Error updating sale',
        cancel: 'Cancel',
        saveChanges: 'Save Changes',
        dateRequired: 'Date is required',
        billNoRequired: 'Bill number is required',
        grandTotalRequired: 'Grand total is required',
        paidRequired: 'Paid amount is required',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this sale?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        loadingUserData: 'Loading shop sales data...',
        noUserRecords: 'No sales found in your shop',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        totalSales: 'Total Sales',
        totalPaid: 'Total Paid',
        totalBalance: 'Total Balance',
        totalAmount: 'Total Amount',
        paymentMethods: {
            cash: 'Cash',
            card: 'Card',
            upi: 'UPI',
            bank_transfer: 'Bank Transfer'
        },
        readonlyNotice: 'Bill number and items cannot be edited after creation',
        walkInCustomer: 'Walk-in Customer',
        notes: 'Notes',
        subtotal: 'Subtotal',
        tax: 'Tax',
        discount: 'Discount',
        grandTotal: 'Grand Total',
        customerAddress: 'Address',
        optional: 'Optional',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        selectShop: 'Please select a shop to view sales',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        invoice: 'INVOICE',
        receipt: 'RECEIPT',
        thankYou: 'Thank you for your business!',
        poweredBy: 'Powered by OurZest',
        cashier: 'Cashier',
        time: 'Time',
        transactionId: 'Transaction ID',
        contactUs: 'Contact Us',
        pageSize: 'Page Size',
        a4: 'A4',
        a5: 'A5',
        thermal: 'Thermal (80mm)',
        printing: 'Printing...',
        printReceipt: 'Print Receipt',
        orientation: 'Orientation',
        portrait: 'Portrait',
        landscape: 'Landscape'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const sellListTranslator = createSellListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// Print Settings Modal Component
// In selllist.js, replace the entire PrintSettingsModal component with this:

const PrintSettingsModal = ({ isOpen, onClose, sale, businessInfo, shopInfo, translations, onPrint }) => {
    const [pageSize, setPageSize] = useState('thermal');
    const [orientation, setOrientation] = useState('portrait');
    const [isPrinting, setIsPrinting] = useState(false);

    // Owner details
    const ownerName = "Muhammad Ahmed"; // Change this to your owner name
    const mobileNumbers = ["03150559988", "03158000062"];

    if (!isOpen || !sale) return null;

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return new Date().toLocaleDateString('ur-PK', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        }
        return date.toLocaleDateString('ur-PK', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatTime = () => {
        return new Date().toLocaleTimeString('ur-PK', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    const handlePrint = async () => {
        setIsPrinting(true);
        
        try {
            const shopName = shopInfo?.name || businessInfo?.businessname || 'دکان کا نام';
            const shopAddress = businessInfo?.businessaddress || '';
            const shopPhone = businessInfo?.phonenumber || '';
            
            // Calculate totals from items if not present
            let subtotal = sale?.subtotal;
            if (!subtotal && sale?.items) {
                subtotal = sale.items.reduce((sum, item) => sum + (parseFloat(item.price) * parseFloat(item.quantity)), 0).toFixed(2);
            }
            subtotal = subtotal || '0.00';
            
            const tax = sale?.tax || '0.00';
            const discount = sale?.discount || '0.00';
            const grandTotal = sale?.grand_total || (parseFloat(subtotal) + parseFloat(tax) - parseFloat(discount)).toFixed(2);
            const paidAmount = sale?.paid_amount || grandTotal;
            const balance = sale?.balance || (parseFloat(grandTotal) - parseFloat(paidAmount)).toFixed(2);
            
            // Urdu labels
            const urdu = {
                invoice: 'انوائس',
                billNo: 'بل نمبر',
                date: 'تاریخ',
                time: 'وقت',
                cashier: 'کیشیر',
                product: 'پروڈکٹ',
                qty: 'مقدار',
                price: 'قیمت (روپے)',
                total: 'کل (روپے)',
                subtotal: 'ذیلی کل',
                tax: 'ٹیکس',
                discount: 'چھوٹ',
                grandTotal: 'کل رقم',
                paid: 'ادا شدہ',
                balance: 'باقی',
                paymentMethod: 'ادائیگی کا طریقہ',
                paymentStatus: 'ادائیگی کی حیثیت',
                paidStatus: 'ادا شدہ',
                thankYou: 'آپ کے کاروبار کا شکریہ!',
                poweredBy: 'طاقت سے',
                owner: 'مالک',
                contact: 'رابطہ نمبر'
            };
            
            // Create receipt HTML
            const isThermal = pageSize === 'thermal';
            const pageWidth = isThermal ? '80mm' : (pageSize === 'a5' ? '148mm' : '210mm');
            
            const receiptHTML = `
                <!DOCTYPE html>
                <html dir="rtl">
                <head>
                    <meta charset="UTF-8">
                    <title>${urdu.invoice} ${sale.bill_number}</title>
                    <style>
                        @import url('https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu&display=swap');
                        @page {
                            size: ${isThermal ? '80mm auto' : `${pageSize} ${orientation}`};
                            margin: ${isThermal ? '2mm' : '5mm'};
                        }
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        body {
                            font-family: 'Noto Nastaliq Urdu', 'Courier New', monospace;
                            background: white;
                            color: black;
                            width: ${pageWidth};
                            margin: 0 auto;
                            padding: ${isThermal ? '3mm' : '10px'};
                            font-size: ${isThermal ? '9pt' : '11pt'};
                            direction: rtl;
                            text-align: right;
                        }
                        .receipt-container {
                            width: 100%;
                        }
                        .text-center { text-align: center; }
                        .text-right { text-align: right; }
                        .text-left { text-align: left; }
                        .font-bold { font-weight: bold; }
                        .border-bottom { border-bottom: 1px dashed #000; }
                        .border-top { border-top: 1px dashed #000; }
                        .border-solid { border-bottom: 1px solid #000; }
                        .mb-1 { margin-bottom: 2mm; }
                        .mb-2 { margin-bottom: 4mm; }
                        .mt-1 { margin-top: 2mm; }
                        .mt-2 { margin-top: 4mm; }
                        .pt-1 { padding-top: 1mm; }
                        .pb-1 { padding-bottom: 1mm; }
                        table { 
                            width: 100%; 
                            border-collapse: collapse;
                            font-size: ${isThermal ? '8pt' : '10pt'};
                        }
                        th { 
                            text-align: center; 
                            border-bottom: 1px solid #000;
                            padding: ${isThermal ? '2px' : '4px'};
                        }
                        td { 
                            padding: ${isThermal ? '2px' : '4px'};
                            text-align: center;
                        }
                        td:first-child {
                            text-align: right;
                        }
                        .shop-name {
                            font-size: ${isThermal ? '14pt' : '18pt'};
                            font-weight: bold;
                            margin-bottom: 2mm;
                        }
                        .owner-name {
                            font-size: ${isThermal ? '10pt' : '12pt'};
                            font-weight: bold;
                            color: #0066cc;
                            margin: 1mm 0;
                        }
                        .invoice-title {
                            font-size: ${isThermal ? '12pt' : '16pt'};
                            font-weight: bold;
                            text-transform: uppercase;
                            margin: 2mm 0;
                        }
                        .contact-line {
                            font-size: ${isThermal ? '7pt' : '9pt'};
                            margin-top: 1mm;
                        }
                        .mobile-numbers {
                            font-size: ${isThermal ? '9pt' : '11pt'};
                            font-weight: bold;
                            color: #0066cc;
                            margin: 2mm 0;
                            text-align: center;
                        }
                        .items-table td, .items-table th {
                            text-align: center;
                        }
                        .items-table td:first-child, .items-table th:first-child {
                            text-align: right;
                        }
                        .totals-row {
                            display: flex;
                            justify-content: space-between;
                            padding: 2px 0;
                        }
                        .grand-total {
                            font-size: ${isThermal ? '11pt' : '14pt'};
                            font-weight: bold;
                            border-top: 1px solid #000;
                            margin-top: 2mm;
                            padding-top: 2mm;
                        }
                        .footer {
                            text-align: center;
                            font-size: ${isThermal ? '7pt' : '9pt'};
                            margin-top: 4mm;
                            padding-top: 2mm;
                            border-top: 1px dashed #000;
                        }
                    </style>
                </head>
                <body>
                    <div class="receipt-container">
                        <!-- Header -->
                        <div class="text-center">
                            <div class="shop-name">${shopName}</div>
                            ${shopAddress ? `<div>${shopAddress}</div>` : ''}
                            ${shopPhone ? `<div>ٹیلی فون: ${shopPhone}</div>` : ''}
                            <div class="owner-name">
                                ${urdu.owner}: ${ownerName}
                            </div>
                            <div class="mobile-numbers">
                                📞 ${mobileNumbers.join('  |  📞 ')}
                            </div>
                        </div>

                        <!-- Title -->
                        <div class="text-center invoice-title">
                            ${urdu.invoice}
                        </div>

                        <!-- Bill Info -->
                        <div style="display: flex; justify-content: space-between; margin-bottom: 2mm;">
                            <span>${urdu.billNo}: #${sale.bill_number}</span>
                            <span>${urdu.date}: ${formatDate(sale.date)}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; margin-bottom: 3mm;">
                            <span>${urdu.time}: ${formatTime()}</span>
                            <span>${urdu.cashier}: ${sale.username || 'اسٹاف'}</span>
                        </div>

                        <!-- Customer Info -->
                        ${(sale.customer_name || sale.customer_phone) ? `
                        <div class="border-top border-bottom pt-1 pb-1 mb-2">
                            ${sale.customer_name ? `<div><strong>${sale.customer_name}</strong></div>` : ''}
                            ${sale.customer_phone ? `<div>فون: ${sale.customer_phone}</div>` : ''}
                            ${sale.customer_address ? `<div>پتہ: ${sale.customer_address}</div>` : ''}
                        </div>
                        ` : ''}

                        <!-- Items Table - FIXED to show product details -->
                        <table class="items-table">
                            <thead>
                                <tr>
                                    <th>${urdu.product}</th>
                                    <th>${urdu.qty}</th>
                                    <th>${urdu.price}</th>
                                    <th>${urdu.total}</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${sale.items && sale.items.length > 0 ? sale.items.map(item => `
                                    <tr>
                                        <td>${item.product_name || item.productName}${item.batch_no ? `<div style="font-size: 7pt;">بیچ: ${item.batch_no}</div>` : ''}${item.barcode ? `<div style="font-size: 6pt;">بارکوڈ: ${item.barcode}</div>` : ''}</td>
                                        <td>${parseFloat(item.quantity).toFixed(2)}</td>
                                        <td>روپے ${parseFloat(item.price).toFixed(2)}</td>
                                        <td>روپے ${parseFloat(item.total || item.price * item.quantity).toFixed(2)}</td>
                                    </tr>
                                `).join('') : `
                                <tr>
                                    <td colspan="4" class="text-center">کوئی مصنوعات نہیں</td>
                                </tr>
                                `}
                            </tbody>
                        </table>

                        <!-- Totals -->
                        <div class="border-top pt-1 mt-1">
                            <div class="totals-row">
                                <span>${urdu.subtotal}:</span>
                                <span>روپے ${parseFloat(subtotal).toFixed(2)}</span>
                            </div>
                            ${parseFloat(tax) > 0 ? `
                            <div class="totals-row">
                                <span>${urdu.tax}:</span>
                                <span>روپے ${parseFloat(tax).toFixed(2)}</span>
                            </div>
                            ` : ''}
                            ${parseFloat(discount) > 0 ? `
                            <div class="totals-row">
                                <span>${urdu.discount}:</span>
                                <span>-روپے ${parseFloat(discount).toFixed(2)}</span>
                            </div>
                            ` : ''}
                            <div class="totals-row grand-total">
                                <span>${urdu.grandTotal}:</span>
                                <span>روپے ${parseFloat(grandTotal).toFixed(2)}</span>
                            </div>
                            <div class="totals-row">
                                <span>${urdu.paid}:</span>
                                <span>روپے ${parseFloat(paidAmount).toFixed(2)}</span>
                            </div>
                            <div class="totals-row">
                                <span>${urdu.balance}:</span>
                                <span>روپے ${parseFloat(balance).toFixed(2)}</span>
                            </div>
                        </div>

                        <!-- Payment Info -->
                        <div class="text-center" style="margin: 2mm 0;">
                            ${urdu.paymentMethod}: ${sale.payment_method?.toUpperCase() || 'نقد'}
                        </div>

                        <div class="text-center font-bold">
                            ${urdu.paymentStatus}: ${sale.payment_status?.toUpperCase() === 'PAID' ? urdu.paidStatus : 'ادھار'}
                        </div>

                        <!-- Notes -->
                        ${sale.notes ? `
                        <div class="text-center" style="font-size: 7pt; margin: 2mm 0;">
                            ${sale.notes}
                        </div>
                        ` : ''}

                        <!-- Footer -->
                        <div class="footer">
                            <div style="font-weight: bold; margin-bottom: 1mm;">
                                ${urdu.thankYou}
                            </div>
                            <div>${urdu.poweredBy} OurZest</div>
                            <div class="contact-line">مدد کے لیے: ${mobileNumbers.join(' | ')}</div>
                        </div>
                    </div>
                </body>
                </html>
            `;
            
            const printWindow = window.open('', '_blank');
            if (printWindow) {
                printWindow.document.write(receiptHTML);
                printWindow.document.close();
                
                printWindow.onload = () => {
                    printWindow.print();
                    printWindow.onafterprint = () => {
                        printWindow.close();
                    };
                };
            }
            
            if (onPrint) onPrint();
            onClose();
        } catch (error) {
            console.error('Print error:', error);
        } finally {
            setIsPrinting(false);
        }
    };

    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className="modal-content-modern print-settings-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
                <div className="modal-header-modern">
                    <div className="modal-icon">
                        <FontAwesomeIcon icon={faPrint} />
                    </div>
                    <div className="modal-title">
                        <h3>رسید پرنٹ کریں</h3>
                    </div>
                    <button className="close-btn-modern" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body-modern" style={{ padding: '20px' }}>
                    <div className="form-group-modern" style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                            <FontAwesomeIcon icon={faRulerCombined} style={{ marginRight: '8px' }} />
                            کاغذ کا سائز:
                        </label>
                        <select
                            value={pageSize}
                            onChange={(e) => setPageSize(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '8px',
                                border: '1px solid #ddd',
                                fontSize: '14px'
                            }}
                        >
                            <option value="thermal">تھرمل (80mm)</option>
                            <option value="a4">A4</option>
                            <option value="a5">A5</option>
                        </select>
                    </div>

                    {pageSize !== 'thermal' && (
                        <div className="form-group-modern" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                                <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: '8px' }} />
                                سمت:
                            </label>
                            <select
                                value={orientation}
                                onChange={(e) => setOrientation(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    fontSize: '14px'
                                }}
                            >
                                <option value="portrait">عمودی</option>
                                <option value="landscape">افقی</option>
                            </select>
                        </div>
                    )}

                    <div className="print-preview-info" style={{
                        background: '#f8f9fa',
                        padding: '12px',
                        borderRadius: '8px',
                        marginTop: '15px',
                        fontSize: '13px',
                        color: '#666'
                    }}>
                        <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '8px' }} />
                        {pageSize === 'thermal' 
                            ? 'تھرمل پیپر پر پرنٹ کریں (80mm چوڑائی)'
                            : `${pageSize.toUpperCase()} پیپر پر ${orientation === 'portrait' ? 'عمودی' : 'افقی'} پرنٹ کریں۔`}
                    </div>
                </div>
                <div className="modal-footer-modern" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '15px', borderTop: '1px solid #eee' }}>
                    <button
                        className="btn-modern btn-secondary-modern"
                        onClick={onClose}
                        disabled={isPrinting}
                    >
                        <FontAwesomeIcon icon={faTimes} style={{ marginRight: '5px' }} />
                        منسوخ کریں
                    </button>
                    <button
                        className="btn-modern btn-primary-modern"
                        onClick={handlePrint}
                        disabled={isPrinting}
                        style={{ background: '#10b981' }}
                    >
                        {isPrinting ? (
                            <>
                                <div className="btn-spinner" style={{ marginRight: '8px' }}></div>
                                پرنٹ ہو رہا ہے...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faPrint} style={{ marginRight: '5px' }} />
                                پرنٹ کریں
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content shop-selector-modal">
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const SellList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [sales, setSales] = useState([]);
    const [filteredSales, setFilteredSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSale, setSelectedSale] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [saleToDelete, setSaleToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        phonenumber: '',
        fullname: '',
        email: ''
    });

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr'
    });

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Edit form state
    const [editFormData, setEditFormData] = useState({
        date: '',
        bill_number: '',
        customer_name: '',
        customer_phone: '',
        customer_address: '',
        subtotal: '',
        tax: '',
        discount: '',
        grand_total: '',
        paid_amount: '',
        balance: '',
        payment_method: 'cash',
        payment_status: 'unpaid',
        notes: '',
        items: []
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // Get theme and language from localStorage
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#10b981';
    });

    const [translations, setTranslations] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Enhanced filter states
    const [filterType, setFilterType] = useState('all');
    const [filterBy, setFilterBy] = useState('all');
    const [billNoFilter, setBillNoFilter] = useState('');
    const [customerFilter, setCustomerFilter] = useState('');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Refs for date inputs
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    // Payment methods for dropdown
    const paymentMethods = [
        { value: 'cash', label: translations.paymentMethods?.cash || 'Cash' },
        { value: 'card', label: translations.paymentMethods?.card || 'Card' },
        { value: 'upi', label: translations.paymentMethods?.upi || 'UPI' },
        { value: 'bank_transfer', label: translations.paymentMethods?.bank_transfer || 'Bank Transfer' }
    ];

    // Calculate totals
    const totalSales = filteredSales.length;
    const totalGrandTotal = filteredSales.reduce((sum, sale) => {
        return sum + (parseFloat(sale.grand_total) || 0);
    }, 0);
    const totalPaid = filteredSales.reduce((sum, sale) => {
        return sum + (parseFloat(sale.paid_amount) || 0);
    }, 0);
    const totalBalance = filteredSales.reduce((sum, sale) => {
        return sum + (parseFloat(sale.balance) || 0);
    }, 0);
    const unpaidCount = filteredSales.filter(sale => {
        const paid = parseFloat(sale.paid_amount) || 0;
        return paid === 0;
    }).length;

    const totalPages = Math.ceil(filteredSales.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentSales = filteredSales.slice(startIndex, endIndex);

    // Function to show toast
    const showToast = (type, message) => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction
        });

        // Auto hide after 3 seconds
        setTimeout(() => {
            setToast(prev => ({ ...prev, show: false }));
        }, 3000);
    };

    // Fetch business info
    const fetchBusinessInfo = async () => {
        if (!user?.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '',
                    bussinesslogo: data.bussinesslogo || '',
                    businessaddress: data.businessaddress || '',
                    phonenumber: data.phonenumber || '',
                    fullname: data.fullname || '',
                    email: data.email || ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
        }
    };

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#10b981';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                document.documentElement.style.setProperty('--customer-primary-color', color);
                document.documentElement.style.setProperty('--customer-primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--customer-primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#10b981';
            setCurrentTheme(theme);
            applyTheme(theme);
        };

        window.addEventListener('storage', handleThemeChange);
        return () => window.removeEventListener('storage', handleThemeChange);
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await sellListTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
    }, [currentLanguage]);

    // Fetch sales when shop changes - ONLY BASED ON SHOP_ID
    useEffect(() => {
        if (currentShop) {
            fetchSales();
            fetchAvailableShops();
            fetchBusinessInfo();
        } else {
            setLoading(false);
        }
    }, [currentShop]);

    // Apply filters
    useEffect(() => {
        applyFilters();
    }, [sales, searchTerm, filterType, filterBy, billNoFilter, customerFilter, phoneFilter, addedByFilter, startDate, endDate]);

    // Fetch available shops
    const fetchAvailableShops = async () => {
        if (!user) return;

        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            setAvailableShops([]);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(
                shop.shop_id,
                shop.shop_name,
                shop.shop_code
            );

            if (success) {
                showToast('success', translations.shopSwitched || 'Shop switched successfully');
            } else {
                showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }

        return (
            <div className="shop-indicator-modern active">
                <div className="shop-badge">
                    <FontAwesomeIcon icon={faStore} />
                    <div className="shop-details">
                        <span className="shop-name">{currentShop.shop_name}</span>
                        <span className="shop-code">({currentShop.shop_code || currentShop.shop_id})</span>
                    </div>
                </div>
                <div className="shop-info-note">
                    {translations.shopSales || 'Showing sales from this shop'}
                </div>
                <button
                    className="btn-modern btn-switch-shop"
                    onClick={() => setShowShopSelector(true)}
                >
                    <FontAwesomeIcon icon={faExchangeAlt} />
                    <span>{translations.switchShop || 'Switch Shop'}</span>
                </button>
            </div>
        );
    };

    const fetchSales = async () => {
        if (!currentShop) {
            showToast('error', translations.noShopSelected || 'Please select a shop first');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            // Get sales based ONLY on shop_id - username is optional
            const response = await fetch('https://ourzestapp.online/oz_backend/sell.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_records',
                    shop_id: currentShop.shop_id  // Only shop_id is required
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setSales(data.data || []);

                if (data.data.length === 0) {
                    showToast('info', translations.noUserRecords || 'No sales found in your shop');
                }
            } else {
                showToast('error', data.message || translations.networkError || 'Network error occurred');
            }
        } catch (error) {
            console.error('Error fetching sales:', error);
            showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...sales];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(sale =>
                sale.bill_number?.toLowerCase().includes(term) ||
                sale.customer_name?.toLowerCase().includes(term) ||
                sale.customer_phone?.toLowerCase().includes(term) ||
                sale.username?.toLowerCase().includes(term)
            );
        }

        if (filterType === 'paid') {
            filtered = filtered.filter(sale => {
                const balance = parseFloat(sale.balance) || 0;
                return balance === 0;
            });
        } else if (filterType === 'unpaid') {
            filtered = filtered.filter(sale => {
                const paid = parseFloat(sale.paid_amount) || 0;
                return paid === 0;
            });
        } else if (filterType === 'partiallyPaid') {
            filtered = filtered.filter(sale => {
                const paid = parseFloat(sale.paid_amount) || 0;
                const grandTotal = parseFloat(sale.grand_total) || 0;
                const balance = parseFloat(sale.balance) || 0;
                return paid > 0 && balance > 0;
            });
        }

        if (filterBy === 'billNo' && billNoFilter.trim()) {
            const billNoTerm = billNoFilter.toLowerCase();
            filtered = filtered.filter(sale =>
                sale.bill_number?.toLowerCase().includes(billNoTerm)
            );
        }

        if (filterBy === 'customer' && customerFilter.trim()) {
            const customerTerm = customerFilter.toLowerCase();
            filtered = filtered.filter(sale =>
                sale.customer_name?.toLowerCase().includes(customerTerm)
            );
        }

        if (filterBy === 'phone' && phoneFilter.trim()) {
            const phoneTerm = phoneFilter.toLowerCase();
            filtered = filtered.filter(sale =>
                sale.customer_phone?.toLowerCase().includes(phoneTerm)
            );
        }

        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const addedByTerm = addedByFilter.toLowerCase();
            filtered = filtered.filter(sale =>
                sale.username?.toLowerCase().includes(addedByTerm)
            );
        }

        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(sale => {
                if (!sale.date) return false;
                const saleDate = new Date(sale.date);
                saleDate.setHours(0, 0, 0, 0);
                return saleDate >= start;
            });
        }

        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(sale => {
                if (!sale.date) return false;
                const saleDate = new Date(sale.date);
                return saleDate <= end;
            });
        }

        setFilteredSales(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterBy('all');
        setBillNoFilter('');
        setCustomerFilter('');
        setPhoneFilter('');
        setAddedByFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
        setFilterType('all');
    };

    const handleView = async (sale) => {
        setSelectedSale(sale);
        
        if (!sale.items && sale.id && currentShop) {
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/sell.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        action: 'get_record',
                        id: sale.id,
                        shop_id: currentShop.shop_id  // Only shop_id is required
                    })
                });

                const data = await response.json();
                if (data.status === 'success' && data.data) {
                    setSelectedSale(data.data);
                }
            } catch (error) {
                console.error('Error fetching sale details:', error);
            }
        }
        
        setShowViewModal(true);
    };

    const handlePrint = (sale) => {
        setSelectedSale(sale);
        setShowPrintModal(true);
    };

    const handleEdit = (sale) => {
        setSelectedSale(sale);
        
        const formattedDate = sale.date ? sale.date.split('T')[0] : '';
        
        setEditFormData({
            id: sale.id,
            date: formattedDate,
            bill_number: sale.bill_number || '',
            customer_name: sale.customer_name || '',
            customer_phone: sale.customer_phone || '',
            customer_address: sale.customer_address || '',
            subtotal: sale.subtotal || '',
            tax: sale.tax || '',
            discount: sale.discount || '',
            grand_total: sale.grand_total || '',
            paid_amount: sale.paid_amount || '',
            balance: sale.balance || '',
            payment_method: sale.payment_method || 'cash',
            payment_status: sale.payment_status || 'unpaid',
            notes: sale.notes || '',
            items: sale.items || []
        });
        setEditFormErrors({});
        setShowEditModal(true);
    };

    const handleDeleteClick = (saleId) => {
        setSaleToDelete(saleId);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!saleToDelete || !currentShop) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/sell.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'delete',
                    id: saleToDelete,
                    shop_id: currentShop.shop_id  // Only shop_id is required
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Sale deleted successfully');
                fetchSales();
                setShowDeleteModal(false);
                setSaleToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting sale');
            }
        } catch (error) {
            console.error('Error deleting sale:', error);
            showToast('error', translations.deleteError || 'Error deleting sale');
        }
    };

    const validateEditForm = () => {
        const newErrors = {};

        if (!editFormData.date) {
            newErrors.date = translations.dateRequired || 'Date is required';
        }

        if (!editFormData.bill_number.trim()) {
            newErrors.bill_number = translations.billNoRequired || 'Bill number is required';
        }

        if (!editFormData.grand_total || parseFloat(editFormData.grand_total) <= 0) {
            newErrors.grand_total = translations.grandTotalRequired || 'Grand total is required';
        }

        if (editFormData.paid_amount === '' || editFormData.paid_amount === null) {
            newErrors.paid_amount = translations.paidRequired || 'Paid amount is required';
        }

        if (editFormData.paid_amount && editFormData.grand_total && parseFloat(editFormData.paid_amount) > parseFloat(editFormData.grand_total)) {
            newErrors.paid_amount = 'Paid amount cannot exceed grand total';
        }

        setEditFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        
        let updatedFormData = { ...editFormData, [name]: value };

        if (name === 'grand_total' || name === 'paid_amount') {
            const grandTotal = parseFloat(name === 'grand_total' ? value : editFormData.grand_total) || 0;
            const paid = parseFloat(name === 'paid_amount' ? value : editFormData.paid_amount) || 0;
            updatedFormData.balance = (grandTotal - paid).toFixed(2);
            
            if (grandTotal > 0) {
                if (paid >= grandTotal) {
                    updatedFormData.payment_status = 'paid';
                } else if (paid === 0) {
                    updatedFormData.payment_status = 'unpaid';
                } else {
                    updatedFormData.payment_status = 'partial';
                }
            }
        }

        setEditFormData(updatedFormData);

        if (editFormErrors[name]) {
            setEditFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdateSale = async (e) => {
        e.preventDefault();

        if (!selectedSale || !currentShop) return;

        if (!validateEditForm()) {
            return;
        }

        setEditLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/sell.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'edit',
                    id: selectedSale.id,
                    date: editFormData.date,
                    bill_number: editFormData.bill_number,
                    customer_name: editFormData.customer_name,
                    customer_phone: editFormData.customer_phone,
                    customer_address: editFormData.customer_address,
                    subtotal: editFormData.subtotal,
                    tax: editFormData.tax,
                    discount: editFormData.discount,
                    grand_total: editFormData.grand_total,
                    paid_amount: editFormData.paid_amount,
                    balance: editFormData.balance,
                    payment_method: editFormData.payment_method,
                    payment_status: editFormData.payment_status,
                    notes: editFormData.notes,
                    shop_id: currentShop.shop_id  // Only shop_id is required
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.saleUpdated || 'Sale updated successfully');
                fetchSales();
                setShowEditModal(false);
                setSelectedSale(null);
                setEditFormErrors({});
            } else {
                showToast('error', data.message || translations.updateError || 'Error updating sale');
            }
        } catch (error) {
            console.error('Error updating sale:', error);
            showToast('error', translations.updateError || 'Error updating sale');
        } finally {
            setEditLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const formatDateGlobal = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            return dateString;
        }
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '-';
        return `Rs. ${parseFloat(amount).toFixed(2)}`;
    };

    const getPaymentStatus = (paid, grandTotal) => {
        const paidAmount = parseFloat(paid) || 0;
        const totalAmount = parseFloat(grandTotal) || 0;
        const balance = totalAmount - paidAmount;
        
        if (balance <= 0) {
            return { status: 'paid', text: translations.paid || 'Paid', color: '#10b981' };
        } else if (paidAmount === 0) {
            return { status: 'unpaid', text: translations.unpaid || 'Unpaid', color: '#ef4444' };
        } else {
            return { status: 'partial', text: translations.partiallyPaid || 'Partial', color: '#f59e0b' };
        }
    };

    const getPaymentIcon = (method) => {
        switch (method) {
            case 'cash':
                return faMoneyBillWave;
            case 'card':
                return faCreditCard;
            case 'upi':
                return faWallet;
            case 'bank_transfer':
                return faUniversity;
            default:
                return faDollarSign;
        }
    };

    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Print Settings Modal */}
            <PrintSettingsModal
                isOpen={showPrintModal}
                onClose={() => setShowPrintModal(false)}
                sale={selectedSale}
                businessInfo={businessInfo}
                shopInfo={currentShop}
                translations={translations}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Header Section */}
            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faShoppingCart} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.salesList || 'Sales List'}</h1>
                        <div className="shop-info">
                            <p>
                                {currentShop?.shop_name
                                    ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name}`
                                    : translations.shopSales || 'Shop Sales'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Shop Indicator */}
                {renderShopIndicator()}
            </div>

            {/* Summary Stats */}
            <div className="summary-stats-modern">
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                        <FontAwesomeIcon icon={faShoppingCart} style={{ color: '#10b981' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totalSales}</div>
                        <div className="stat-label">{translations.totalSales || 'Total Sales'}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                        <FontAwesomeIcon icon={faDollarSign} style={{ color: '#3b82f6' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalGrandTotal)}</div>
                        <div className="stat-label">{translations.totalAmount || 'Total Amount'}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
                        <FontAwesomeIcon icon={faMoneyBillWave} style={{ color: '#f59e0b' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalPaid)}</div>
                        <div className="stat-label">{translations.totalPaid || 'Total Paid'}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ef4444' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalBalance)}</div>
                        <div className="stat-label">{translations.totalBalance || 'Total Balance'}</div>
                    </div>
                </div>
            </div>

            {/* Advanced Filters Section */}
            <div className="advanced-filters-section">
                <div className="filters-row">
                    {/* Filter By Dropdown */}
                    <div className="filter-group compact">
                        <select
                            value={filterBy}
                            onChange={(e) => setFilterBy(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.filterBy || 'Filter By'}</option>
                            <option value="billNo">{translations.billNoFilter || 'Bill Number'}</option>
                            <option value="customer">{translations.customerFilter || 'Customer Name'}</option>
                            <option value="phone">{translations.phone || 'Phone'}</option>
                            <option value="added_by">{translations.addedByFilter || 'Added By'}</option>
                            <option value="date">{translations.dateRange || 'Date Range'}</option>
                        </select>
                    </div>

                    {filterBy === 'billNo' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={billNoFilter}
                                onChange={(e) => setBillNoFilter(e.target.value)}
                                placeholder={translations.billNoFilter || 'Bill number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'customer' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={customerFilter}
                                onChange={(e) => setCustomerFilter(e.target.value)}
                                placeholder={translations.customerFilter || 'Customer name...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'phone' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={phoneFilter}
                                onChange={(e) => setPhoneFilter(e.target.value)}
                                placeholder={translations.phone || 'Phone number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'added_by' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={addedByFilter}
                                onChange={(e) => setAddedByFilter(e.target.value)}
                                placeholder={translations.addedByFilter || 'Added by username...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'date' && (
                        <>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={startDateRef}
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.startDate || 'Start Date'}
                                />
                            </div>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={endDateRef}
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.endDate || 'End Date'}
                                />
                            </div>
                        </>
                    )}

                    {/* Payment Status Filter */}
                    <div className="filter-group compact">
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.paymentStatus || 'Payment Status'}</option>
                            <option value="paid">{translations.paidFilter || 'Paid'}</option>
                            <option value="unpaid">{translations.unpaidFilter || 'Unpaid'}</option>
                            <option value="partiallyPaid">{translations.partiallyPaidFilter || 'Partially Paid'}</option>
                        </select>
                    </div>

                    {/* Search Box */}
                    <div className="filter-group compact search-group">
                        <div className="search-box compact">
                            <input
                                type="text"
                                placeholder={translations.searchPlaceholder || 'Search sales in shop...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    </div>

                    {/* Refresh Button */}
                    <div className="filter-group compact">
                        <button
                            onClick={fetchSales}
                            className="refresh-btn-modern compact"
                            disabled={loading}
                            title={translations.refresh || 'Refresh'}
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">{translations.refresh || 'Refresh'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>{translations.loadingUserData || 'Loading shop sales data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="no-shop-container">
                    <div className="no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view sales'}</p>
                        <button
                            className="btn-modern btn-primary-modern"
                            onClick={() => setShowShopSelector(true)}
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Table Section */}
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="centered-cell">{translations.date || 'Date'}</th>
                                            <th className="centered-cell">{translations.billNumber || 'Bill No'}</th>
                                            <th className="centered-cell">{translations.customerName || 'Customer'}</th>
                                            <th className="centered-cell">{translations.items || 'Items'}</th>
                                            <th className="centered-cell">{translations.grandTotal || 'Total'}</th>
                                            <th className="centered-cell">{translations.paidAmount || 'Paid'}</th>
                                            <th className="centered-cell">{translations.balance || 'Balance'}</th>
                                            <th className="centered-cell">{translations.paymentStatus || 'Status'}</th>
                                            <th className="centered-cell">{translations.paymentMethod || 'Payment'}</th>
                                            <th className="centered-cell">{translations.addedBy || 'Added By'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentSales.map((sale, index) => {
                                            const paymentStatus = getPaymentStatus(sale.paid_amount, sale.grand_total);
                                            const itemCount = sale.items ? sale.items.length : (sale.item_count || '—');
                                            const addedBy = sale.username || '-';
                                            
                                            return (
                                                <tr key={sale.id}>
                                                    <td className="id-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faHashtag} className="cell-icon" />
                                                            <span className="cell-text">{startIndex + index + 1}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faCalendar} className="cell-icon" />
                                                            <span className="cell-text">{formatDateGlobal(sale.date)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faReceipt} className="cell-icon" />
                                                            <span className="cell-text" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{sale.bill_number}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faUser} className="cell-icon" />
                                                            <span className="cell-text">{sale.customer_name || (translations.walkInCustomer || 'Walk-in')}</span>
                                                            {sale.customer_phone && (
                                                                <div className="cell-subtext">
                                                                    <FontAwesomeIcon icon={faPhone} size="xs" />
                                                                    <span>{sale.customer_phone}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="items-badge">
                                                                <FontAwesomeIcon icon={faBox} />
                                                                <span>{itemCount}</span>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="grand-total" style={{ color: '#059669', fontWeight: 'bold' }}>
                                                                {formatCurrency(sale.grand_total)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="paid-amount" style={{ color: '#3b82f6', fontWeight: 'bold' }}>
                                                                {formatCurrency(sale.paid_amount)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className={`balance-amount ${parseFloat(sale.balance) > 0 ? 'has-balance' : 'no-balance'}`} 
                                                                  style={{ 
                                                                      color: parseFloat(sale.balance) > 0 ? '#d97706' : '#059669',
                                                                      fontWeight: 'bold'
                                                                  }}>
                                                                {formatCurrency(sale.balance)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <div 
                                                                className="payment-status-badge"
                                                                style={{ 
                                                                    backgroundColor: paymentStatus.color + '20',
                                                                    color: paymentStatus.color,
                                                                    border: `1px solid ${paymentStatus.color}40`
                                                                }}
                                                            >
                                                                {paymentStatus.text}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <div className="payment-method">
                                                                <FontAwesomeIcon icon={getPaymentIcon(sale.payment_method)} />
                                                                <span>{translations.paymentMethods?.[sale.payment_method] || sale.payment_method}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className={`added-by-badge ${addedBy === user?.username ? 'self' : ''}`}>
                                                                <FontAwesomeIcon icon={faUser} className="cell-icon" size="xs" />
                                                                <span className="cell-text">{addedBy}</span>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="actions-cell-modern centered-cell">
                                                        <div className="cell-content centered-content actions-content">
                                                            <button
                                                                onClick={() => handleView(sale)}
                                                                className="action-btn-modern view-btn-modern"
                                                                title={translations.view || 'View'}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                            <button
                                                                onClick={() => handlePrint(sale)}
                                                                className="action-btn-modern print-btn-modern"
                                                                title={translations.print || 'Print'}
                                                            >
                                                                <FontAwesomeIcon icon={faPrint} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEdit(sale)}
                                                                className="action-btn-modern edit-btn-modern"
                                                                title={translations.edit || 'Edit'}
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteClick(sale.id)}
                                                                className="action-btn-modern delete-btn-modern"
                                                                title={translations.delete || 'Delete'}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {/* No Data Message */}
                                {currentSales.length === 0 && !loading && currentShop && (
                                    <div className="no-data-modern">
                                        <FontAwesomeIcon icon={faShoppingCart} />
                                        <p>{translations.noUserRecords || 'No sales found in your shop'}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {filteredSales.length > 0 && (
                        <div className="pagination-modern">
                            <div className="pagination-info">
                                <span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(endIndex, filteredSales.length)} {translations.of || 'of'} {filteredSales.length} {translations.sales || 'sales'}</span>
                                {currentShop?.shop_name && (
                                    <span className="shop-info-pagination">
                                        | {translations.shopName || 'Shop'}: {currentShop.shop_name}
                                    </span>
                                )}
                                <span style={{ marginLeft: '20px', color: '#059669', fontWeight: '600' }}>
                                    {translations.totalAmount || 'Total Amount'}: {formatCurrency(totalGrandTotal)}
                                </span>
                            </div>
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>

                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedSale && (
                <div className="modal-overlay-modern" onClick={() => setShowViewModal(false)}>
                    <div className="modal-content-modern view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faReceipt} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.saleDetails || 'Sale Details'}</h3>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern view-modal-body">
                            <div className="detail-section">
                                <h4 className="section-title">{translations.saleInfo || 'Sale Information'}</h4>
                                <div className="details-grid">
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.saleId || 'Sale ID'}:</span>
                                        <span className="detail-value">#{selectedSale.id}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.date || 'Date'}:</span>
                                        <span className="detail-value">{formatDateGlobal(selectedSale.date)}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.billNumber || 'Bill Number'}:</span>
                                        <span className="detail-value" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{selectedSale.bill_number}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.customerName || 'Customer Name'}:</span>
                                        <span className="detail-value">{selectedSale.customer_name || (translations.walkInCustomer || 'Walk-in Customer')}</span>
                                    </div>
                                    {selectedSale.customer_phone && (
                                        <div className="detail-row-modern">
                                            <span className="detail-label">{translations.phone || 'Phone'}:</span>
                                            <span className="detail-value">{selectedSale.customer_phone}</span>
                                        </div>
                                    )}
                                    {selectedSale.customer_address && (
                                        <div className="detail-row-modern">
                                            <span className="detail-label">{translations.customerAddress || 'Address'}:</span>
                                            <span className="detail-value">{selectedSale.customer_address}</span>
                                        </div>
                                    )}
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.paymentMethod || 'Payment Method'}:</span>
                                        <span className="detail-value">
                                            <FontAwesomeIcon icon={getPaymentIcon(selectedSale.payment_method)} />
                                            {translations.paymentMethods?.[selectedSale.payment_method] || selectedSale.payment_method}
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.paymentStatus || 'Payment Status'}:</span>
                                        <span className="detail-value">
                                            <div className="payment-status-badge" style={{ 
                                                backgroundColor: getPaymentStatus(selectedSale.paid_amount, selectedSale.grand_total).color + '20',
                                                color: getPaymentStatus(selectedSale.paid_amount, selectedSale.grand_total).color,
                                                border: `1px solid ${getPaymentStatus(selectedSale.paid_amount, selectedSale.grand_total).color}40`,
                                                display: 'inline-block'
                                            }}>
                                                {getPaymentStatus(selectedSale.paid_amount, selectedSale.grand_total).text}
                                            </div>
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.grandTotal || 'Grand Total'}:</span>
                                        <span className="detail-value" style={{ color: '#059669', fontWeight: 'bold' }}>{formatCurrency(selectedSale.grand_total)}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.paidAmount || 'Paid Amount'}:</span>
                                        <span className="detail-value" style={{ color: '#3b82f6', fontWeight: 'bold' }}>{formatCurrency(selectedSale.paid_amount)}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.balance || 'Balance'}:</span>
                                        <span className="detail-value" style={{ color: parseFloat(selectedSale.balance) > 0 ? '#d97706' : '#059669', fontWeight: 'bold' }}>
                                            {formatCurrency(selectedSale.balance)}
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.addedBy || 'Added By'}:</span>
                                        <span className="detail-value">
                                            <span className="added-by-badge" style={{ background: '#e0e7ff', color: '#4f46e5' }}>
                                                <FontAwesomeIcon icon={faUser} size="xs" />
                                                {selectedSale.username || '-'}
                                            </span>
                                        </span>
                                    </div>
                                    {selectedSale.notes && (
                                        <div className="detail-row-modern full-width">
                                            <span className="detail-label">{translations.notes || 'Notes'}:</span>
                                            <span className="detail-value notes-text">{selectedSale.notes}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Items Section */}
                            {selectedSale.items && selectedSale.items.length > 0 && (
                                <div className="detail-section">
                                    <h4 className="section-title">{translations.items || 'Sale Items'}</h4>
                                    <div className="items-table-container">
                                        <table className="items-table">
                                            <thead>
                                                <tr>
                                                    <th>{translations.serialNo || '#'}</th>
                                                    <th>{translations.itemProduct || 'Product'}</th>
                                                    <th>{translations.itemBatch || 'Batch'}</th>
                                                    <th>{translations.itemUnit || 'Unit'}</th>
                                                    <th>{translations.itemPrice || 'Price'}</th>
                                                    <th>{translations.itemQuantity || 'Qty'}</th>
                                                    <th>{translations.itemTotal || 'Total'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedSale.items.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{idx + 1}</td>
                                                        <td>{item.product_name}</td>
                                                        <td className="batch-cell">{item.batch_no}</td>
                                                        <td>{item.unit}</td>
                                                        <td className="amount-cell">{formatCurrency(item.price)}</td>
                                                        <td className="amount-cell">{parseFloat(item.quantity).toFixed(2)}</td>
                                                        <td className="amount-cell total">{formatCurrency(item.total)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="6" className="text-right">{translations.subtotal || 'Subtotal'}:</td>
                                                    <td className="amount-cell">{formatCurrency(selectedSale.subtotal)}</td>
                                                </tr>
                                                {parseFloat(selectedSale.tax) > 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="text-right">{translations.tax || 'Tax'}:</td>
                                                        <td className="amount-cell">{formatCurrency(selectedSale.tax)}</td>
                                                    </tr>
                                                )}
                                                {parseFloat(selectedSale.discount) > 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="text-right">{translations.discount || 'Discount'}:</td>
                                                        <td className="amount-cell">-{formatCurrency(selectedSale.discount)}</td>
                                                    </tr>
                                                )}
                                                <tr className="grand-total-row">
                                                    <td colSpan="6" className="text-right">{translations.grandTotal || 'Grand Total'}:</td>
                                                    <td className="amount-cell grand-total">{formatCurrency(selectedSale.grand_total)}</td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="6" className="text-right">{translations.paidAmount || 'Paid'}:</td>
                                                    <td className="amount-cell paid">{formatCurrency(selectedSale.paid_amount)}</td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="6" className="text-right">{translations.balance || 'Balance'}:</td>
                                                    <td className="amount-cell balance">{formatCurrency(selectedSale.balance)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                {translations.close || 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedSale && (
                <div className="modal-overlay-modern edit-modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content-modern edit-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="form-header-modern edit-form-header">
                            <div className="header-content">
                                <div className="header-icon">
                                    <FontAwesomeIcon icon={faUserEdit} />
                                </div>
                                <div className="header-text">
                                    <h1>{translations.editSale || 'Edit Sale'}</h1>
                                    <p>{translations.fillDetails || 'Fill in the sale details below'}</p>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleUpdateSale} className="modern-form edit-form">
                            <div className="form-grid-modern edit-form-grid">
                                <div className={`form-group-modern ${editFormErrors.date ? 'has-error' : ''}`}>
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faCalendar} className="input-icon" />
                                        <input
                                            type="date"
                                            id="date"
                                            name="date"
                                            value={editFormData.date}
                                            onChange={handleEditFormChange}
                                            required
                                            max={getTodayDate()}
                                            disabled={editLoading}
                                            dir="ltr"
                                        />
                                        <label htmlFor="date" className="input-label">
                                            {translations.date || 'Date'}
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.date && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.date}
                                        </div>
                                    )}
                                </div>

                                <div className={`form-group-modern ${editFormErrors.bill_number ? 'has-error' : ''}`}>
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faReceipt} className="input-icon" />
                                        <input
                                            type="text"
                                            id="bill_number"
                                            name="bill_number"
                                            value={editFormData.bill_number}
                                            onChange={handleEditFormChange}
                                            required
                                            placeholder={translations.billNumber || 'Enter bill number'}
                                            disabled={true}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                            className="readonly-input"
                                        />
                                        <label htmlFor="bill_number" className="input-label">
                                            {translations.billNumber || 'Bill Number'}
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.bill_number && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.bill_number}
                                        </div>
                                    )}
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faUser} className="input-icon" />
                                        <input
                                            type="text"
                                            id="customer_name"
                                            name="customer_name"
                                            value={editFormData.customer_name}
                                            onChange={handleEditFormChange}
                                            placeholder={translations.customerName || 'Enter customer name'}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <label htmlFor="customer_name" className="input-label">
                                            {translations.customerName || 'Customer Name'}
                                            <span className="optional">{translations.optional || ' Optional'}</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faPhone} className="input-icon" />
                                        <input
                                            type="tel"
                                            id="customer_phone"
                                            name="customer_phone"
                                            value={editFormData.customer_phone}
                                            onChange={handleEditFormChange}
                                            placeholder={translations.phone || 'Enter phone number'}
                                            disabled={editLoading}
                                            dir="ltr"
                                        />
                                        <label htmlFor="customer_phone" className="input-label">
                                            {translations.phone || 'Phone'}
                                            <span className="optional">{translations.optional || ' Optional'}</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="input-icon" />
                                        <select
                                            id="payment_method"
                                            name="payment_method"
                                            value={editFormData.payment_method}
                                            onChange={handleEditFormChange}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        >
                                            {paymentMethods.map(method => (
                                                <option key={method.value} value={method.value}>
                                                    {method.label}
                                                </option>
                                            ))}
                                        </select>
                                        <label htmlFor="payment_method" className="input-label">
                                            {translations.paymentMethod || 'Payment Method'}
                                        </label>
                                    </div>
                                </div>

                                <div className={`form-group-modern ${editFormErrors.grand_total ? 'has-error' : ''}`}>
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faDollarSign} className="input-icon" />
                                        <input
                                            type="number"
                                            id="grand_total"
                                            name="grand_total"
                                            value={editFormData.grand_total}
                                            onChange={handleEditFormChange}
                                            required
                                            step="0.01"
                                            min="0"
                                            placeholder="0.00"
                                            disabled={editLoading}
                                            dir="ltr"
                                        />
                                        <label htmlFor="grand_total" className="input-label">
                                            {translations.grandTotal || 'Grand Total'}
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.grand_total && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.grand_total}
                                        </div>
                                    )}
                                </div>

                                <div className={`form-group-modern ${editFormErrors.paid_amount ? 'has-error' : ''}`}>
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="input-icon" />
                                        <input
                                            type="number"
                                            id="paid_amount"
                                            name="paid_amount"
                                            value={editFormData.paid_amount}
                                            onChange={handleEditFormChange}
                                            required
                                            step="0.01"
                                            min="0"
                                            placeholder="0.00"
                                            disabled={editLoading}
                                            dir="ltr"
                                        />
                                        <label htmlFor="paid_amount" className="input-label">
                                            {translations.paidAmount || 'Paid Amount'}
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.paid_amount && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.paid_amount}
                                        </div>
                                    )}
                                </div>

                                <div className="form-group-modern">
                                    <div className="calculation-display">
                                        <div className="calculation-item">
                                            <span className="calculation-label">
                                                <FontAwesomeIcon icon={faCalculator} />
                                                {translations.balance || 'Balance'}:
                                            </span>
                                            <span className={`calculation-value ${parseFloat(editFormData.balance) > 0 ? 'has-balance' : 'no-balance'}`}
                                                  style={{ color: parseFloat(editFormData.balance) > 0 ? '#d97706' : '#059669' }}>
                                                Rs. {parseFloat(editFormData.balance || 0).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="form-group-modern full-width">
                                <div className="input-container">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="input-icon" />
                                    <textarea
                                        id="customer_address"
                                        name="customer_address"
                                        value={editFormData.customer_address}
                                        onChange={handleEditFormChange}
                                        rows="2"
                                        placeholder={translations.customerAddress || 'Enter customer address'}
                                        disabled={editLoading}
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label htmlFor="customer_address" className="input-label">
                                        {translations.customerAddress || 'Address'}
                                        <span className="optional">{translations.optional || ' Optional'}</span>
                                    </label>
                                </div>
                            </div>

                            <div className="form-group-modern full-width">
                                <div className="input-container">
                                    <FontAwesomeIcon icon={faStickyNote} className="input-icon" />
                                    <textarea
                                        id="notes"
                                        name="notes"
                                        value={editFormData.notes}
                                        onChange={handleEditFormChange}
                                        rows="2"
                                        placeholder={translations.notes || 'Enter any additional notes'}
                                        disabled={editLoading}
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label htmlFor="notes" className="input-label">
                                        {translations.notes || 'Notes'}
                                        <span className="optional">{translations.optional || ' Optional'}</span>
                                    </label>
                                </div>
                            </div>

                            <div className="form-actions-modern edit-form-actions">
                                <button
                                    type="button"
                                    className="btn-modern btn-secondary-modern"
                                    onClick={() => setShowEditModal(false)}
                                    disabled={editLoading}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                    <span>{translations.cancel || 'Cancel'}</span>
                                </button>
                                <button
                                    type="submit"
                                    className="btn-modern btn-primary-modern"
                                    disabled={editLoading}
                                >
                                    {editLoading ? (
                                        <>
                                            <div className="btn-spinner"></div>
                                            <span>{translations.updating || 'Updating...'}</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>{translations.saveChanges || 'Save Changes'}</span>
                                            <FontAwesomeIcon icon={faSave} />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>

                        <div className="edit-customer-info">
                            <p><strong>{translations.saleId || 'Sale ID'}:</strong> #{selectedSale.id}</p>
                            <p><strong>{translations.billNumber || 'Bill No'}:</strong> {selectedSale.bill_number}</p>
                            <p><strong>{translations.shopName || 'Shop'}:</strong> {currentShop?.shop_name || 'N/A'}</p>
                            <p className="readonly-notice">
                                <FontAwesomeIcon icon={faInfoCircle} />
                                {translations.readonlyNotice || 'Bill number and items cannot be edited after creation'}
                            </p>
                        </div>

                        {editLoading && (
                            <div className="loading-overlay edit-loading-overlay">
                                <div className="loading-content">
                                    <div className="loading-spinner"></div>
                                    <p>{translations.updating || 'Updating...'}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content-modern delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern delete-header">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </div>
                            <div className="modal-title">
                                <h3 style={{ color: 'var(--customer-error-color)' }}>
                                    {translations.confirmDelete || 'Confirm Delete'}
                                </h3>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="delete-message">
                                <p style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--customer-text-primary)' }}>
                                    {translations.deleteMessage || 'Are you sure you want to delete this sale?'}
                                </p>
                                <p style={{ fontSize: '0.9rem', color: 'var(--customer-warning-color)' }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                                    {translations.deleteWarning || 'This action cannot be undone.'}
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                type="button"
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                {translations.noCancel || 'No, Cancel'}
                            </button>
                            <button
                                type="button"
                                className="btn-modern btn-delete-modern"
                                onClick={handleDelete}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                {translations.yesDelete || 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SellList;

